// pushActions.js — the app's side of a push action button (2026-10-28).
// Server: api/lib/push-actions.js attaches "Mark done" / "Mark paid" with
// { action, habit_id | bill_id, for_date | for_month }. OneSignal buttons
// always open the app, so src/App.js routes the tap (routeNotificationClick)
// into a pending action here (localStorage, because the tap may cold-launch
// the process, plus a window event for the warm case), and src/Scorify.jsx
// consumes it once auth has resolved, data is loaded and the app lock is
// passed. A button tap NEVER opens the coach chat: when the habit or bill is
// not found the app says so and opens the tab instead. Pure, no React:
// tested in tests/unit/push-actions.test.mjs. Copy carries no dashes.
import { habitTemplateLabel, habitDisplayName } from './habitTemplates.js';

export const PENDING_ACTION_KEY = 'scorify_pending_action';
export const PENDING_ACTION_EVENT = 'scorify-pending-action';
export const PENDING_ACTION_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export const WRONG_DAY_NOTICE = 'That reminder was for yesterday';
export const WRONG_MONTH_NOTICE = 'That reminder was for last month';
export const HABIT_NOT_FOUND_NOTICE = "Couldn't find that habit";
export const BILL_NOT_FOUND_NOTICE = "Couldn't find that bill";

const ACTIONS = new Set(['habit_done', 'bill_paid']);

/** Lowercase, runs of anything but letters and digits become one underscore. */
export function normalizeSlug(s) {
  return String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * The action button id a click carries, or null for a body tap. The plugin
 * reports it as result.actionId; the iOS SDK also writes the selected button
 * into the payload's additional data as actionSelected, read as a fallback.
 */
export function clickActionId({ actionId, data } = {}) {
  const a = String(actionId || (data && data.actionSelected) || '').trim();
  return ACTIONS.has(a) ? a : null;
}

const str = (v) => { const s = String(v ?? '').trim(); return s || null; };

/**
 * The pending action a click describes, or null when the tap was not one of
 * our buttons. The BUTTON decides the action; the data only identifies the
 * habit or bill. Missing or malformed data still yields a pending action
 * (id null), so the app can say it could not find the habit instead of
 * falling through to the coach chat.
 */
export function pendingActionFromClick({ actionId, data, now = Date.now() } = {}) {
  const action = clickActionId({ actionId, data });
  if (!action) return null;
  const d = data && typeof data === 'object' ? data : {};
  if (action === 'habit_done') {
    const id = str(d.habit_id) || str(d.habit_slug) || str(d.slug);
    const fd = String(d.for_date || '').slice(0, 10);
    return { action, id, for_date: /^\d{4}-\d{2}-\d{2}$/.test(fd) ? fd : null, received_at: now };
  }
  const id = str(d.bill_id);
  const fm = String(d.for_month || '').slice(0, 7);
  return { action, id, for_month: /^\d{4}-\d{2}$/.test(fm) ? fm : null, received_at: now };
}

/**
 * Where a notification click goes. Action buttons win over everything else:
 *   { route: 'action',   pending }  "Mark done" / "Mark paid" (writePendingAction)
 *   { route: 'briefing', pending }  daily check-in / weekly recap (plan_week sets plan)
 *   { route: 'mood',     pending }  evening mood prompt
 *   { route: 'bank_reconnect', pending: { itemId } }  broken bank link: Money tab reconnect
 *   { route: 'coach',    pending }  body tap on a Kai alert: reopen the text in the drawer
 *   { route: 'none' }               nothing to open
 */
export const PENDING_BANK_RECONNECT_KEY = 'scorify_pending_bank_reconnect';
export const BANK_RECONNECT_EVENT = 'scorify-bank-reconnect-notification';

export function routeNotificationClick({ actionId, data, body, notificationId, now = Date.now() } = {}) {
  const extra = data && typeof data === 'object' ? data : {};
  const pending = pendingActionFromClick({ actionId, data: extra, now });
  if (pending) return { route: 'action', pending };
  if (extra.st_type === 'briefing' || extra.st_type === 'recap') {
    const planWeek = extra.st_type === 'recap' && actionId === 'plan_week';
    return { route: 'briefing', pending: { kind: extra.st_type, briefingId: extra.st_briefing_id || null, date: extra.st_date || null, ts: now, ...(planWeek ? { plan: true } : {}) } };
  }
  if (extra.st_type === 'mood') return { route: 'mood', pending: { date: extra.st_date || null, ts: now } };
  // Bank reconnect push (api/lib/bank-reconnect.js): open the Money tab and the
  // reconnect flow for that Item, never the coach drawer.
  if (extra.st_type === 'bank_reconnect') return { route: 'bank_reconnect', pending: { itemId: extra.st_item_id ? String(extra.st_item_id) : null, ts: now } };
  const text = extra.st_message || body || '';
  if (text) return { route: 'coach', pending: { message: text, logId: extra.st_log_id || null, onesignalId: notificationId ? String(notificationId) : null, ts: now } };
  return { route: 'none', pending: null };
}

export function writePendingAction(pending) {
  try { localStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(pending)); } catch {}
  try { window.dispatchEvent(new CustomEvent(PENDING_ACTION_EVENT, { detail: pending })); } catch {}
}

export function readPendingAction() {
  try {
    const raw = localStorage.getItem(PENDING_ACTION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && ACTIONS.has(p.action) ? p : null;
  } catch { return null; }
}

export function clearPendingAction() {
  try { localStorage.removeItem(PENDING_ACTION_KEY); } catch {}
}

/**
 * The client habit a push refers to. Accepts the row id (habits.id, what the
 * server sends), the template slug (habits.name, the client id), or a
 * slugified label ("drink-8-glasses" finds "Drink 8 Glasses"), so a rename
 * or a legacy key still resolves.
 */
export function findHabit(habits, ref) {
  const r = str(ref);
  if (!r) return null;
  const n = normalizeSlug(r);
  return (habits || []).find((h) => h && h.active !== false && (
    String(h._dbId || '') === r || String(h.id || '') === r || String(h.name || '') === r
    || (n && (normalizeSlug(h.id) === n || normalizeSlug(h.name) === n
      || (h.label && normalizeSlug(h.label) === n)
      || normalizeSlug(habitTemplateLabel(h.id || h.name)) === n))
  )) || null;
}

/** The bill a push refers to: by row id, else by slugified name. */
export function findBill(bills, ref) {
  const r = str(ref);
  if (!r) return null;
  const n = normalizeSlug(r);
  return (bills || []).find((b) => b && (String(b.id) === r || (n && b.name && normalizeSlug(b.name) === n))) || null;
}

/**
 * What the app should do with a pending action. Returns one of:
 *   { kind: 'drop', reason }                       nothing to do (stale, unknown)
 *   { kind: 'gated' }                              route through requirePro
 *   { kind: 'notice', message, tab }               say why, open a tab (not found, wrong day)
 *   { kind: 'habit_log', habitId, name, alreadyDone, toast }
 *   { kind: 'bill_paid', billId, name, alreadyPaid, toast }
 * today: local 'YYYY-MM-DD'; month: local 'YYYY-MM'; habits: client rows
 * (id = template slug, _dbId, label, lastLoggedDate); bills: rows (id, name,
 * status, paid_for_month); gated: the paywall gate for this user.
 */
export function resolvePendingAction(pending, { today, month, habits = [], bills = [], gated = false, now = Date.now() } = {}) {
  if (!pending || !ACTIONS.has(pending.action)) return { kind: 'drop', reason: 'unknown' };
  const age = now - Number(pending.received_at || 0);
  if (!Number.isFinite(age) || age < 0 || age > PENDING_ACTION_MAX_AGE_MS) return { kind: 'drop', reason: 'stale' };
  if (gated) return { kind: 'gated' };
  if (pending.action === 'habit_done') {
    const habit = findHabit(habits, pending.id);
    if (!habit) return { kind: 'notice', message: HABIT_NOT_FOUND_NOTICE, tab: 'habits' };
    if (pending.for_date !== today) return { kind: 'notice', message: WRONG_DAY_NOTICE, tab: 'habits' };
    const name = habitDisplayName(habit);
    return { kind: 'habit_log', habitId: habit.id, name, alreadyDone: habit.lastLoggedDate === today, toast: `Logged: ${name}` };
  }
  const bill = findBill(bills, pending.id);
  if (!bill) return { kind: 'notice', message: BILL_NOT_FOUND_NOTICE, tab: 'finances' };
  if (pending.for_month !== month) return { kind: 'notice', message: WRONG_MONTH_NOTICE, tab: 'finances' };
  const alreadyPaid = bill.paid_for_month === month;
  const name = bill.name || 'bill';
  return { kind: 'bill_paid', billId: bill.id, name, alreadyPaid, toast: `Marked paid: ${name}` };
}
